import { Component, OnInit, AfterContentInit, DoCheck, Input, ViewChild } from '@angular/core';
import { SignatureFieldComponent } from 'app/_components/signature-field/signature-field.component';
import { isTableRowBlank, isTableType, rowHasFile, ROW_HEADER_KEY, TABLE_FILE_KEY } from 'app/_utils/question-table.helper';
import { parseAndSanitizeMarkdown } from 'app/_utils/safe-markdown';

declare var $: any;

@Component({
  selector: 'app-question',
  templateUrl: './question.component.html',
})
export class QuestionComponent implements OnInit, AfterContentInit, DoCheck {
  @Input() question: any;
  @Input() answers: any;
  @Input() choices: any;
  @Input() noQuestionText: any;
  @Input() questionClass: string = 'h4';
  @Input() questionHeaderClass: string = 'h3';
  @Input() disabled: boolean = false;
  /** Sibling questions on the same form, in the order they appear on it. */
  @Input() siblingQuestions: any[] = [];
  /** True when this instance renders a single cell inside a TABLE. */
  @Input() isCell: boolean = false;
  /** Uniquifies form control names so a column repeated down rows doesn't collide. */
  @Input() nameScope: string = '';
  /** Per-cell required, computed by the parent TABLE. */
  @Input() required: boolean = false;
  @ViewChild(SignatureFieldComponent) public signatureField: SignatureFieldComponent;

  ROW_HEADER_KEY = ROW_HEADER_KEY;
  TABLE_FILE_KEY = TABLE_FILE_KEY;
  private cellQuestionCache = new Map<any, { __src: any, q: any }>();

  /** Always-required questions (and parent-forced cells) show an asterisk and HTML required. */
  get isFieldRequired(): boolean {
    return this.required || !!this.question?.always_required;
  }

  /** API-supplied question labels must be sanitized before [innerHTML] binding. */
  get sanitizedText(): string {
    return parseAndSanitizeMarkdown(this.question?.text ?? '');
  }

  ngOnInit() {
    const vchoices = this.choices ?? this.question?.choices;
    if (!vchoices) {
      this.choices = this.question?.other ? ['Other'] : [];
    } else {
      const parsed = (Array.isArray(vchoices) ? vchoices : String(vchoices).split('\n'))
        .map((value) => String(value).trim())
        .filter(Boolean);
      if (this.question?.other) {
        const withoutOther = parsed.filter((choice) => choice.toLowerCase() !== 'other');
        this.choices = [...withoutOther, 'Other'];
      } else {
        this.choices = parsed;
      }
    }
    this.ensureRows();
  }

  get showOtherField(): boolean {
    if (!this.question?.other) return false;
    const value = this.answers?.[this.question.id];
    if (Array.isArray(value)) {
      return value.some((item) => String(item).trim().toLowerCase() === 'other');
    }
    return String(value ?? '').trim().toLowerCase() === 'other';
  }

  ngAfterContentInit() {

  }

  ngDoCheck() {
    // Answers often arrive after ngOnInit (nested subscribes) and are mutated in
    // place, so ngOnChanges never fires. ngDoCheck runs before this view is
    // checked, so seeding rows here cannot trigger ExpressionChangedAfterChecked.
    this.ensureRows();
  }

  get isTable(): boolean {
    return this.question?.type_id === 'TABLE' && !this.isCell;
  }

  /** TABLE_FILE renders a table whose first column is an uploaded-file column. */
  get isTableFile(): boolean {
    return this.question?.type_id === 'TABLE_FILE' && !this.isCell;
  }

  /** True for any table-like question (shares column/row mechanics). */
  get isAnyTable(): boolean {
    return this.isTable || this.isTableFile;
  }

  /** A COLUMN_HEADER renders only as a column inside its TABLE, never standalone. */
  get isHidden(): boolean {
    return this.question?.type_id === 'COLUMN_HEADER' && !this.isCell;
  }

  /**
   * COLUMN_HEADER questions linked to this TABLE, in the order they appear on the
   * form. siblingQuestions arrives pre-ordered, so filtering preserves that order.
   */
  get columns(): any[] {
    if (!isTableType(this.question?.type_id) || !this.question?.id) return [];
    const tableId = Number(this.question.id);
    return (this.siblingQuestions || []).filter((q) =>
      q && q.type_id === 'COLUMN_HEADER' && !q.archived &&
      (q.table_question_ids || []).map(Number).includes(tableId));
  }

  get rowHeaders(): string[] {
    const rh = this.question?.row_headers;
    if (!rh) return [];
    return String(rh).split('\n').map((s) => s.trim()).filter(Boolean);
  }

  get rows(): any[] {
    const v = this.answers?.[this.question?.id];
    return Array.isArray(v) ? v : [];
  }

  /**
   * The question a cell renders as: the column's own properties under its cell
   * type. Cached so the binding keeps a stable identity across change detection.
   */
  cellQuestion(col: any): any {
    const cached = this.cellQuestionCache.get(col.id);
    if (cached && cached.__src === col && cached.q.type_id === col.column_type_id) {
      return cached.q;
    }
    const q = { ...col, type_id: col.column_type_id };
    this.cellQuestionCache.set(col.id, { __src: col, q });
    return q;
  }

  cellNameScope(rowIndex: number): string {
    return `${this.question.id}_${rowIndex}_`;
  }

  /**
   * A table with columns always shows at least one row; a table with row headers
   * shows one row per header, reattaching saved values by header text.
   */
  private ensureRows(): void {
    if (!this.isAnyTable || !this.question?.id || !this.answers) return;
    const key = this.question.id;
    const current = Array.isArray(this.answers[key]) ? this.answers[key] : [];
    const headers = this.rowHeaders;
    let next = current;

    if (headers.length) {
      const fixed = headers.map((h) =>
        current.find((r) => r && r[ROW_HEADER_KEY] === h) || { [ROW_HEADER_KEY]: h });
      const extras = current.filter((r) => r && !r[ROW_HEADER_KEY]);
      next = [...fixed, ...extras];
    } else if ((this.columns.length || this.isTableFile) && current.length === 0) {
      // A file table always owns a File Name column, so it shows a row even with
      // no other column headers.
      next = [{}];
    }

    // Only reassign when the row set actually changed, or *ngFor thrashes every pass.
    const unchanged = Array.isArray(this.answers[key])
      && this.answers[key].length === next.length
      && next.every((r, i) => this.answers[key][i] === r);
    if (!unchanged) this.answers[key] = next;
  }

  addRow(): void {
    if (!this.question?.allow_add_rows) return;
    this.rows.push({});
  }

  /** Row-header rows are fixed; only user-added rows can be removed. */
  canRemoveRow(i: number): boolean {
    return !!this.question?.allow_add_rows && !this.rows[i]?.[ROW_HEADER_KEY] && !this.disabled;
  }

  removeRow(i: number): void {
    if (this.canRemoveRow(i)) this.rows.splice(i, 1);
  }

  isRowBlank(row: any): boolean {
    return isTableRowBlank(row, this.columns);
  }

  /**
   * A row with a row header follows its column's rule outright; a user-added row
   * only does so once something in it has been filled in, so blank added rows are
   * left alone.
   */
  isCellRequired(row: any, col: any): boolean {
    if (!col.always_required) return false;
    if (row[ROW_HEADER_KEY]) return true;
    return !this.isRowBlank(row);
  }

  onFileChange(event) {
    const reader = new FileReader();
    if (event.target.files && event.target.files.length) {
      const [file] = event.target.files;
      reader.readAsDataURL(file);
      reader.onload = () => {
        this.answers[this.question.id] = {
          name: file.name,
          type: file.type,
          size: file.size,
          data: reader.result
        };
      };
    }
  }

  // ----- TABLE_FILE helpers -----

  /** Display name of a row's uploaded file, if any. */
  rowFileName(row: any): string {
    return row?.[TABLE_FILE_KEY]?.name || '';
  }

  /** True when a row already carries a file (freshly picked or previously saved). */
  rowHasFile(row: any): boolean {
    return rowHasFile(row);
  }

  /** Read a picked file to a base64 object, then hand it to `done`. */
  private readFile(event: any, done: (file: any) => void): void {
    const input = event?.target;
    if (!input?.files?.length) return;
    const [file] = input.files;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      done({ name: file.name, type: file.type, size: file.size, data: reader.result });
      // Reset so re-picking the same file still fires (change).
      input.value = '';
    };
  }

  /** Set/replace the file on an existing row (per-row File Name upload button). */
  onRowFileChange(event: any, row: any): void {
    if (this.disabled) return;
    this.readFile(event, (file) => { row[TABLE_FILE_KEY] = file; });
  }

  /** Upload button under the table: each upload appends a new row carrying the file. */
  onUploadNewRow(event: any): void {
    if (this.disabled || !this.question?.allow_add_rows) return;
    this.readFile(event, (file) => { this.rows.push({ [TABLE_FILE_KEY]: file }); });
  }

  /** Delete a file row (the trash icon in the Remove column). */
  removeFileRow(i: number): void {
    if (this.disabled) return;
    if (this.rows[i]?.[ROW_HEADER_KEY]) return; // fixed header rows stay
    this.rows.splice(i, 1);
  }
}
